import {
  type QueryClient,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { CreditBalance } from '@repo/contracts';

import { canvasKeys } from '../api/canvas-captures';
import { apiFetch } from '../api/client';

/**
 * Nested under `canvasKeys.all` on purpose.
 *
 * A balance belongs to the active scope — acting as a team spends the team's
 * credits — and `AccountSwitcher` invalidates the whole canvas root when the
 * scope changes, precisely because every id under it is derived from that
 * scope. A key outside that root would keep showing the previous account's
 * balance until something else happened to refetch it.
 */
export const creditKeys = {
  balance: () => [...canvasKeys.all, 'credits'] as const,
};

const EMPTY: CreditBalance = {
  baseCredits: 0,
  topupCredits: 0,
  totalCredits: 0,
};

export function useCredits(): {
  balance: CreditBalance;
  /**
   * Whether a run of this price can be started.
   *
   * **True while the balance is still unknown**, and that is the important
   * part. The client is advisory here — the server gates every charged route
   * and answers `INSUFFICIENT_CREDITS`, which the panels render properly — so
   * a slow or failed balance request must not be able to disable the whole
   * product. Blocking on an unloaded figure means a user on a bad connection
   * finds every button dead with no explanation, which is a worse failure than
   * letting a doomed request through and reporting what came back.
   */
  canAfford: (cost: number) => boolean;
  /** False until the first response — the pill renders nothing until then
   *  rather than flashing a zero at somebody who has plenty. */
  loaded: boolean;
} {
  const { data } = useQuery({
    queryFn: () => apiFetch<CreditBalance>('/canvas/credits'),
    queryKey: creditKeys.balance(),
    // Never `Infinity`: the balance moves on the server, not in this tab. A
    // run started on another device, a
    // teammate spending the team's credits — all of them make a cached figure
    // wrong with nothing here to notice.
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const loaded = data !== undefined;

  return {
    balance: data ?? EMPTY,
    canAfford: (cost) => !loaded || (data?.totalCredits ?? 0) >= cost,
    loaded,
  };
}

/**
 * Refetch the balance after something has been spent.
 *
 * Called from the mutations that charge rather than from the components that
 * render the number: the pill is in the top bar of a screen a modal
 * covers, so nothing about it re-renders on its own when a run starts. Without
 * this the figure only moves on a reload, and a user who just spent 50 credits
 * watching it not move concludes the run was free.
 */
export function invalidateCredits(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: creditKeys.balance() });
}

/** The hook form, for a component that already has the client. */
export function useInvalidateCredits(): () => void {
  const queryClient = useQueryClient();
  return () => {
    invalidateCredits(queryClient);
  };
}
